
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import DayPartSelect from "@/components/inputs/DayPartSelect.vue";
import {opcionalesQueCambian} from "@/helpers/ventana-al-guardar";

/** Los extras que ofrece ahora una actividad del catálogo (ninguno si no está). */
function extrasDeLaActividad(actividades: any[], actividadId: any): any[] {
	const actividad = actividades.find((una) => una.id == actividadId);
	return actividad ? actividad.extras || [] : [];
}

/**
 * N-286 · Los extras elegidos de una media jornada frente al catálogo, con la regla del
 * backend al copiar un presupuesto (`BudgetCopyService::opcionalesDelTramo`): cada extra
 * se busca en el catálogo de SU actividad —la que lo ofrece, que en los paquetes de la
 * decisión 6A es otra del mismo viaje («Ruta de Paddle Surf» en la Ruta del Cares)—; sigue
 * si su tarifa está; si no, pasa al del mismo nombre de esa actividad (el mismo extra en
 * otro tramo de personas, el de menor id); si no hay ninguno, se cae.
 * `duenos` recuerda qué actividad ofrecía cada extra la última vez que se vio.
 *
 * Con `actividadNueva` (el operador acaba de cambiar la actividad) solo sigue lo que
 * ofrece la nueva: los extras de la anterior el servidor se los cobraba a ella.
 */
function extrasDelCatalogo(elegidos: any[], actividades: any[], actividadId: any, duenos: any, actividadNueva: boolean): any[] {
	const resultado: any[] = [];
	for (const extra of elegidos || []) {
		let delCatalogo;
		if (actividadNueva) {
			delCatalogo = extrasDeLaActividad(actividades, actividadId).find((opcion) => opcion.id == extra.id);
		} else {
			const queLoOfrece = actividades.find((una) => (una.extras || []).some((opcion) => opcion.id == extra.id));
			if (queLoOfrece) {
				duenos[extra.id] = queLoOfrece.id;
			}
			const catalogo = extrasDeLaActividad(actividades, duenos[extra.id] || actividadId);
			const nombre = String(extra.name || '').trim();
			delCatalogo = catalogo.find((opcion) => opcion.id == extra.id) || catalogo
				.filter((opcion) => String(opcion.name || '').trim() === nombre)
				.sort((a, b) => a.id - b.id)[0];
			// Un extra que ningún catálogo ha ofrecido nunca no sabemos de qué actividad es
			// (6A con su actividad despublicada, que el catálogo no trae): se queda como
			// estaba y decide el servidor, que lo admite si es del viaje y cubre al grupo.
			if (!delCatalogo && !duenos[extra.id]) {
				delCatalogo = extra;
			}
		}
		if (delCatalogo && !resultado.some((ya) => ya.id == delCatalogo.id)) {
			resultado.push(delCatalogo);
		}
	}
	return resultado;
}

/**
 * N-288 · 6A: la línea de uno o varios extras de la misma procedencia (textos del 8-oct).
 * `deDonde`: «de «Actividad»» o, sin dueño conocido, «que hoy no ofrece ninguna actividad…».
 */
function textoExtrasDeOtraActividad(cuantos: number, deDonde: string): string {
	if (cuantos === 1) {
		return `Es un extra ${deDonde}. Se puede quitar aquí.`;
	}
	const quienes = cuantos === 2 ? 'Los dos son' : (cuantos === 3 ? 'Los tres son' : 'Son');
	return `${quienes} extras ${deDonde}. Se pueden quitar aquí.`;
}

/**
 * La actividad de la que es un extra: la que lo ofrece en el catálogo, la que lo ofrecía
 * la última vez (`duenos`, N-286) o, si llegó el catálogo de otro tramo, la ÚNICA que ofrece
 * uno con su nombre. Si son varias o ninguna, no se adivina.
 */
function duenoDelExtra(extra: any, actividades: any[], duenos: any): any {
	const porId = actividades.find((una) => (una.extras || []).some((opcion) => opcion.id == extra.id))
		|| actividades.find((una) => una.id == duenos[extra.id]);
	if (porId) {
		return porId;
	}
	const nombre = String(extra.name || '').trim();
	const porNombre = actividades.filter((una) => (una.extras || [])
		.some((opcion) => String(opcion.name || '').trim() === nombre));
	return porNombre.length === 1 ? porNombre[0] : null;
}

/**
 * N-288 · 6A — lo que enseña el selector de extras de un hueco: los extras de su actividad
 * y, detrás, los ELEGIDOS que son de otra actividad del paquete (antes se cobraban sin
 * verse), con una línea por actividad que dice de dónde vienen (`duenoDelExtra`).
 * Un extra ajeno que se quita deja de estar elegido y sale de la lista.
 */
function listaDeExtras(elegidos: any[], propios: any[], actividades: any[], duenos: any): any {
	const ajenos = (elegidos || []).filter((extra) => !propios.some((suyo) => suyo.id == extra.id));
	const grupos: any[] = [];
	for (const extra of ajenos) {
		const dueno = duenoDelExtra(extra, actividades, duenos);
		const nombre = dueno ? dueno.name : null;
		const grupo = grupos.find((uno) => uno.nombre === nombre);
		if (grupo) {
			grupo.cuantos++;
		} else {
			grupos.push({ nombre, cuantos: 1 });
		}
	}
	const linea = grupos
		.map((grupo) => textoExtrasDeOtraActividad(grupo.cuantos, grupo.nombre
			? `de «${grupo.nombre}»`
			: 'que hoy no ofrece ninguna actividad para este grupo'))
		.join(' ');
	return { items: propios.concat(ajenos), linea };
}

@Component({
	components: {DayPartSelect}
})
export default class ActivitySelect extends Vue {
	@Prop({ default: null, required: false }) private model;
	@Prop({ default: null, required: true }) private day;
	@Prop({ default: null, required: true }) private people;
	@Prop({ default: null, required: true }) private errors;
	@Prop({ default: null, required: false }) private valid;
	@Prop({ default: null, required: true }) private activities;

	private half_day_activities: any[] = [];
	private full_day_activities: any[] = [];
	private items_morning_extras: any[] = [];
	private items_evening_extras: any[] = [];
	private items_full_day_extras: any[] = [];
	// N-286: los extras que PIDIÓ el operador en cada media jornada. Cada catálogo nuevo se
	// aplica sobre esto y no sobre lo que dejó el anterior: al teclear «29» pasa un catálogo
	// de «2» sin esos extras, y aplicarlo encima los borraba para siempre.
	private extrasPedidos: any = {};
	// N-286: qué actividad del catálogo ofrecía cada extra la última vez que se vio (id → id).
	private duenoDeCadaExtra: any = {};
	// N-288 · 6A: la línea gris bajo cada selector de extras («Es un extra de «…» (paquete)…»).
	private avisoExtrasDeOtraActividad: any = { day_part_1: '', day_part_2: '', day_part_3: '' };

	private value = {
		daypart: null,
		day_part_1: {
			day: null,
			people: null,
			daypart: 1,
			activity_id: null,
			budget_activity_id: null,
			prices: [],
			prices_to_display: [],
			extras: []
		},
		day_part_2: {
			day: null,
			people: null,
			daypart: 2,
			activity_id: null,
			budget_activity_id: null,
			prices: [],
			prices_to_display: [],
			extras: []
		},
		day_part_3: {
			day: null,
			people: null,
			daypart: 3,
			activity_id: null,
			budget_activity_id: null,
			prices: [],
			prices_to_display: [],
			extras: []
		}
	};

	// @Watch("model")
	// model_change(value) {
	// 	this.value = value;
	// }

	@Watch("people")
	people_change(value) {
		this.value.day_part_1.people = value;
		this.value.day_part_2.people = value;
		this.value.day_part_3.people = value;
	}

	// N-264: el catálogo se vuelve a pedir cuando cambian los pasajeros, y una actividad
	// puede cobrar por TRAMOS (la «Visita guiada» de Londres: una tarifa hasta 30 personas
	// y otra desde 31). Las medias jornadas ya elegidas toman la tarifa del catálogo
	// nuevo; con la vieja, el servidor no encuentra ninguna válida y borra la actividad.
	@Watch("activities")
	activities_change(values) {
		this.prepareOptions();
		this.refreshPrices();
		this.$emit('setPrice', this.value);
	}

	constructor() {
		super();
	}

	mounted() {
		if (this.activities.length) {
			this.prepareOptions();
		}
		if (this.model.daypart) {
			this.copy();
		} else {
			this.reset();
		}
	}

	reset() {
		this.extrasPedidos = {};
		this.avisoExtrasDeOtraActividad = { day_part_1: '', day_part_2: '', day_part_3: '' };
		this.value = {
			daypart: null,
			day_part_1: {
				day: this.day,
				people: this.people,
				daypart: 1,
				activity_id: null,
				budget_activity_id: null,
				prices_to_display: [],
				prices: [],
				extras: []
			},
			day_part_2: {
				day: this.day,
				people: this.people,
				daypart: 2,
				activity_id: null,
				budget_activity_id: null,
				prices_to_display: [],
				prices: [],
				extras: []
			},
			day_part_3: {
				day: this.day,
				people: this.people,
				daypart: 3,
				activity_id: null,
				budget_activity_id: null,
				prices_to_display: [],
				prices: [],
				extras: []
			}
		};
	}

	copy() {
		this.value = this.model;
		this.refreshPrices();
		// this.emitValue(this.value);
	}

	/** Las tarifas de cada media jornada elegida, sacadas del catálogo que hay ahora. */
	refreshPrices() {
		if (this.value.day_part_1.activity_id) {
			this.setExtrasAndPrices('day_part_1', 'morning', true);
		}
		if (this.value.day_part_2.activity_id) {
			this.setExtrasAndPrices('day_part_2', 'evening', true);
		}
		if (this.value.day_part_3.activity_id) {
			this.setExtrasAndPrices('day_part_3', '', true);
		}
	}


	/**
	 * `mismaActividad`: el catálogo se ha renovado (pasajeros) y la actividad no ha cambiado.
	 * `morning_or_evening` ya no se usa (la lista sale de `day_part`, N-288), pero sigue en la
	 * firma: las llamadas pasan `mismaActividad` en tercera posición.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	setExtrasAndPrices(day_part, morning_or_evening = '', mismaActividad = false){
		this.ponerListaDeExtras(day_part, null);
		// N-264: las tarifas solo se sustituyen si la actividad ESTÁ en el catálogo. Una que
		// ya no sale (despublicada después de presupuestarla) conserva las suyas: vaciarlas
		// hacía que el servidor la borrara al guardar cualquier otra cosa.
		for (const activity of this.activities) {
			if (activity.id == this.value[day_part].activity_id) {
				this.value[day_part].prices = [];
				this.value[day_part].prices_to_display = activity.prices;
				for (const price of activity.prices) {
					this.value[day_part].prices.push(price.id);
				}
				const pedidos = mismaActividad
					? (this.extrasPedidos[day_part] || this.value[day_part].extras || [])
					: extrasDelCatalogo(this.value[day_part].extras, this.activities, activity.id, this.duenoDeCadaExtra, true);
				this.extrasPedidos[day_part] = pedidos;
				this.value[day_part].extras = extrasDelCatalogo(pedidos, this.activities, activity.id, this.duenoDeCadaExtra, false);
				this.ponerListaDeExtras(day_part, activity);
			}
		}
	}

	/**
	 * N-288 · 6A: la lista del selector de extras de un hueco y su línea gris. Se guardan en
	 * datos, no se calculan al pintar: unos `items` nuevos en cada render rompen el
	 * autocompletado múltiple de Vuetify (rule `panel-admin`). Sin actividad, vacíos.
	 */
	ponerListaDeExtras(day_part, actividad) {
		const lista = actividad
			? listaDeExtras(this.value[day_part].extras, actividad.extras || [], this.activities, this.duenoDeCadaExtra)
			: { items: [], linea: '' };
		this.avisoExtrasDeOtraActividad[day_part] = lista.linea;
		if (day_part == 'day_part_3') {
			this.items_full_day_extras = lista.items;
		} else if (day_part == 'day_part_1') {
			this.items_morning_extras = lista.items;
		} else if (day_part == 'day_part_2') {
			this.items_evening_extras = lista.items;
		}
	}

	/** N-286: lo que el operador elige a mano en el selector de extras pasa a ser lo pedido. */
	elegirExtras(day_part) {
		this.extrasPedidos[day_part] = (this.value[day_part].extras || []).slice();
		// N-288 · 6A: un extra de otra actividad que se quita sale de la lista y de la línea.
		const actividad = this.activities.find((una) => una.id == this.value[day_part].activity_id);
		if (actividad) {
			this.ponerListaDeExtras(day_part, actividad);
		}
		this.emitValue(this.value);
	}

	/**
	 * N-288 · T3 · Los extras PEDIDOS de una media jornada que el catálogo de ahora quitó o pasó a otro
	 * de otro precio (N-286). Sin catálogo leído para ese hueco, nada.
	 */
	extrasQueCambian(day_part) {
		const pedidos = this.extrasPedidos[day_part];
		return pedidos ? opcionalesQueCambian(pedidos, this.value[day_part].extras || []) : { quitados: [], cambiados: [] };
	}

	clearDayParts() {
		const currentDayPart = this.value.daypart;
		this.reset();
		this.value.daypart = currentDayPart;
		this.emitValue(this.value)
	}

	prepareOptions() {
		this.half_day_activities = [];
		this.full_day_activities = [];
		for (const activity of this.activities) {
			if (activity.time_type == 1) {
				this.half_day_activities.push({'id': activity.id, 'name': activity.name});
			}else{
				this.full_day_activities.push({'id': activity.id, 'name': activity.name});
			}
		}
	}

	emitValue(value){
		this.$emit('input', value);
		this.$emit('setPrice', value);
	}
}
